import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useUrlParams, useUrlState } from '../hooks/useUrlState';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import { api, downloadFile } from '../lib/api';
import { fmtCurrency, fmtMonth } from '../lib/fmt';
import {
  SectionLabel, Card, Table, Tr, Td, StatusChip, ActionButton,
  FilterBar, Select, DrillPanel, Spinner, EmptyState, MetricCard, IssueTypeBadge, SortableTh,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';

export default function DeliverablesPage() {
  const { user } = useAuth();
  const { config } = useConfig();
  const [filters, patchFilters] = useUrlParams({ period: 'all', status: 'all', type: 'all' });
  const [openIdRaw, setOpenIdRaw] = useUrlState('open', '');
  const openId = openIdRaw || null;
  const setOpenId = (id: string | null) => setOpenIdRaw(id ?? '');
  const periodFilter = filters.period;
  const statusFilter = filters.status;
  const typeFilter   = filters.type;

  const params: Record<string, string> = {};
  if (periodFilter !== 'all') params.month = periodFilter;
  if (statusFilter !== 'all') params.status = statusFilter;
  if (typeFilter === 'Deliverable' || typeFilter === 'Overhead') params.issue_type = typeFilter;

  const { data, isLoading, error } = useQuery(
    () => api.scorecard.deliverables(user!.jira_user_id, params),
    [user?.jira_user_id, periodFilter, statusFilter, typeFilter],
  );

  const deliverables = data?.deliverables ?? [];

  // Bucket each row. Using attribution_month (not just d.status) is deliberate:
  // an Overhead ships when its parent Engagement ships, and keeps its own
  // status = 'In Progress' even then — attribution_month is the shared signal.
  // Canceled is separated from WIP so it doesn't inflate in-progress counts.
  const bucketOf = (d: typeof deliverables[number]): 'shipped' | 'wip' | 'canceled' =>
    d.status === 'Canceled' ? 'canceled'
    : d.attribution_month ? 'shipped'
    : 'wip';

  const shipped  = deliverables.filter((d) => bucketOf(d) === 'shipped');
  const wip      = deliverables.filter((d) => bucketOf(d) === 'wip');
  const canceled = deliverables.filter((d) => bucketOf(d) === 'canceled');
  const totalShippedValue  = shipped.reduce((s, d) => s + Number(d.shipped_value), 0);
  const totalWipValue      = wip.reduce((s, d) => s + Number(d.shipped_value), 0);
  const totalCanceledValue = canceled.reduce((s, d) => s + Number(d.shipped_value), 0);

  // Unique months for filter
  const months = [...new Set(deliverables.map((d) => d.attribution_month).filter(Boolean) as string[])].sort().reverse();

  const openDel = deliverables.find((d) => d.jira_issue_id === openId);

  const { sorted: sortedDeliverables, sort } = useTableSort(
    deliverables,
    {
      deliverable: (d) => d.summary,
      engagement:  (d) => d.tempo_account_name ?? '',
      status:      (d) => d.status,
      assignee:    (d) => (user?.role === 'IC' ? d.delivery_manager_display_name : d.solution_architect_display_name) ?? '',
      hours:       (d) => user?.role === 'IC' ? Number(d.my_billable_hours) : Number(d.total_billable_hours),
      rate:        (d) => Number(d.billable_rate),
      value:       (d) => Number(d.shipped_value),
      period:      (d) => d.attribution_month ?? '',
    },
    { key: 'period', dir: 'desc' },
  );

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${canceled.length > 0 ? 5 : 4}, minmax(0,1fr))`, gap: 10 }}>
        <MetricCard label="Total deliverables" value={deliverables.length} />
        <MetricCard label="Shipped" value={shipped.length} accent="green" sub={fmtCurrency(totalShippedValue)} />
        <MetricCard label="In progress" value={wip.length} accent="amber" sub={fmtCurrency(totalWipValue) + ' WIP'} />
        {canceled.length > 0 && (
          <MetricCard label="Canceled" value={canceled.length} sub={fmtCurrency(totalCanceledValue) + ' forfeited'} />
        )}
        <MetricCard label="Avg billable rate" value={deliverables.length ? `$${Math.round(deliverables.reduce((s, d) => s + Number(d.billable_rate), 0) / deliverables.length)}/h` : '—'} />
      </div>

      <div>
        <SectionLabel>Deliverables</SectionLabel>
        <Card>
          <FilterBar>
            <Select value={periodFilter} onChange={(v) => patchFilters({ period: v })}>
              <option value="all">All periods</option>
              {months.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </Select>
            <Select value={statusFilter} onChange={(v) => patchFilters({ status: v })}>
              <option value="all">All statuses</option>
              <option value="Shipped">Shipped</option>
              <option value="WIP">In progress</option>
              <option value="Canceled">Canceled</option>
            </Select>
            <Select value={typeFilter} onChange={(v) => patchFilters({ type: v })}>
              <option value="all">Deliverables + Overheads</option>
              <option value="Deliverable">Deliverables only</option>
              <option value="Overhead">Overheads only</option>
            </Select>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-muted)' }}>
              {deliverables.length} deliverables
            </span>
            <ActionButton
              onClick={() => {
                const qs = new URLSearchParams({ ...params, format: 'csv' }).toString();
                downloadFile(`/scorecard/${user!.jira_user_id}/deliverables?${qs}`,
                  `deliverables-${user!.jira_user_id}.csv`);
              }}
              variant="default"
              disabled={deliverables.length === 0}
            >
              Export CSV
            </ActionButton>
          </FilterBar>

          {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : !deliverables.length ? (
            <EmptyState message="No deliverables match the current filters." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <SortableTh columnKey="deliverable" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Deliverable</SortableTh>
                  <SortableTh columnKey="engagement"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Engagement</SortableTh>
                  <SortableTh columnKey="status"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Status</SortableTh>
                  <SortableTh columnKey="assignee"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>{user?.role === 'IC' ? 'DM' : 'IC'}</SortableTh>
                  <SortableTh columnKey="hours"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>{user?.role === 'IC' ? 'My hours' : 'Total hours'}</SortableTh>
                  <SortableTh columnKey="rate"        sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Rate</SortableTh>
                  <SortableTh columnKey="value"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>{user?.role === 'IC' ? 'My contribution' : 'Shipped value'}</SortableTh>
                  <SortableTh columnKey="period"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Period</SortableTh>
                </tr>
              </thead>
              <tbody>
                {sortedDeliverables.map((d) => (
                  <Tr
                    key={d.jira_issue_id}
                    onClick={() => setOpenId(openId === d.jira_issue_id ? null : d.jira_issue_id)}
                    selected={openId === d.jira_issue_id}
                  >
                    <Td bold>
                      <IssueTypeBadge issueType={d.issue_type} />
                      {d.summary}{' '}
                      {(() => {
                        const url = jiraIssueUrl(config, d.jira_issue_key);
                        return url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ fontSize: 10, color: 'var(--color-blue)', marginLeft: 4 }}
                          >
                            {d.jira_issue_key} ↗
                          </a>
                        ) : (
                          <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 4 }}>
                            {d.jira_issue_key}
                          </span>
                        );
                      })()}
                    </Td>
                    <Td muted style={{ fontSize: 11 }}>{d.tempo_account_name}</Td>
                    <Td><StatusChip status={d.status} /></Td>
                    <Td muted style={{ fontSize: 11 }}>
                      {user?.role === 'IC' ? d.delivery_manager_display_name : d.solution_architect_display_name}
                    </Td>
                    <Td>{user?.role === 'IC' ? `${d.my_billable_hours}h` : `${d.total_billable_hours}h`}</Td>
                    <Td>${d.billable_rate}/h</Td>
                    {(() => {
                      const b = bucketOf(d);
                      const color = b === 'shipped' ? 'var(--color-green)'
                                  : b === 'canceled' ? 'var(--color-red)'
                                  : 'var(--color-amber)';
                      const tag = b === 'shipped' ? null
                                : b === 'canceled' ? ' canceled'
                                : ' WIP';
                      return (
                        <Td bold style={{ color }}>
                          {fmtCurrency(Number(d.shipped_value))}
                          {tag && <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--color-text-muted)' }}>{tag}</span>}
                        </Td>
                      );
                    })()}
                    <Td muted style={{ fontSize: 11 }}>{d.attribution_month ? fmtMonth(d.attribution_month) : 'Unshipped'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      {openDel && (
        <DrillPanel
          title={openDel.summary}
          meta={<span>{openDel.tempo_account_name}{openDel.parent_engagement_summary ? ` · ${openDel.parent_engagement_summary}` : ''}</span>}
          onClose={() => setOpenId(null)}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, padding: '12px 16px' }}>
            <MetricCard label="Status" value={<StatusChip status={openDel.status} />} />
            <MetricCard
              label={user?.role === 'IC' ? 'Delivery manager' : 'Solution architect'}
              value={<span style={{ fontSize: 13 }}>{user?.role === 'IC' ? openDel.delivery_manager_display_name : openDel.solution_architect_display_name}</span>}
            />
            <MetricCard
              label={user?.role === 'IC' ? 'My billable hours' : 'Total billable hours'}
              value={`${user?.role === 'IC' ? openDel.my_billable_hours : openDel.total_billable_hours}h`}
            />
            {(() => {
              const b = bucketOf(openDel);
              return (
                <MetricCard
                  label={b === 'shipped' ? 'Shipped value' : b === 'canceled' ? 'Forfeited value' : 'WIP value'}
                  value={fmtCurrency(Number(openDel.shipped_value))}
                  accent={b === 'shipped' ? 'green' : b === 'canceled' ? undefined : 'amber'}
                />
              );
            })()}
          </div>
        </DrillPanel>
      )}
    </>
  );
}
